// Force-directed layout for the Neighbour graph tab (Analysis > MeshCore > Neighbour graph). A small, dependency-free
// version of the d3-force recipe: Barnes-Hut many-body repulsion, link springs, a weak pull to the middle, and the
// same alpha cooling, so a 700-node / 2,000-link graph settles in well under a second and then stops. No imports on
// purpose (compiled alone and covered by node:test, frontend/tests/forceLayout.test.mjs). Deterministic: the start
// positions are a fixed spiral and nothing uses Math.random.

export interface LayoutLink {
  a: number;
  b: number;
}

export interface LayoutOptions {
  /** Spring rest length between linked nodes. */
  linkDistance?: number;
  /** Repulsion strength between all nodes (negative pushes apart, d3 style). */
  charge?: number;
  /** Barnes-Hut opening angle: larger is faster and rougher. */
  theta?: number;
  /** Pull towards the middle, so unconnected pieces stay in view. */
  gravity?: number;
  velocityDecay?: number;
  alphaMin?: number;
  /** Ticks for alpha to fall from 1 to alphaMin. */
  coolingTicks?: number;
}

export interface Layout {
  xs: Float64Array;
  ys: Float64Array;
  /** Current temperature: 1 at the start, below alphaMin means settled. */
  alpha: number;
  settled: boolean;
  /** Runs up to `n` ticks (stops early once settled); returns how many ran. */
  step(n: number): number;
}

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** The start positions: a sunflower spiral, so no two nodes coincide and the spread is even. */
export function initialPositions(n: number, spacing = 10): { xs: Float64Array; ys: Float64Array } {
  const xs = new Float64Array(n);
  const ys = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const r = spacing * Math.sqrt(i + 0.5);
    const a = i * GOLDEN_ANGLE;
    xs[i] = r * Math.cos(a);
    ys[i] = r * Math.sin(a);
  }
  return { xs, ys };
}

// --- quadtree (flat arrays, rebuilt every tick) ---------------------------------------------------------------

class Quadtree {
  cap: number;
  size = 0;
  x0: Float64Array;
  y0: Float64Array;
  w: Float64Array;
  kids: Int32Array; // 4 per cell, -1 = none
  leaf: Int32Array; // point index held by a leaf cell, -1 = none
  mass: Float64Array;
  cx: Float64Array;
  cy: Float64Array;

  constructor(points: number) {
    this.cap = Math.max(16, points * 4 + 16);
    this.x0 = new Float64Array(this.cap);
    this.y0 = new Float64Array(this.cap);
    this.w = new Float64Array(this.cap);
    this.kids = new Int32Array(this.cap * 4);
    this.leaf = new Int32Array(this.cap);
    this.mass = new Float64Array(this.cap);
    this.cx = new Float64Array(this.cap);
    this.cy = new Float64Array(this.cap);
  }

  private cell(x0: number, y0: number, w: number): number {
    const c = this.size++;
    if (c >= this.cap) throw new Error("quadtree full");
    this.x0[c] = x0;
    this.y0[c] = y0;
    this.w[c] = w;
    this.leaf[c] = -1;
    this.kids[c * 4] = this.kids[c * 4 + 1] = this.kids[c * 4 + 2] = this.kids[c * 4 + 3] = -1;
    this.mass[c] = 0;
    return c;
  }

  private quadrant(c: number, x: number, y: number): number {
    const half = this.w[c] / 2;
    return (x >= this.x0[c] + half ? 1 : 0) + (y >= this.y0[c] + half ? 2 : 0);
  }

  private child(c: number, q: number): number {
    let k = this.kids[c * 4 + q];
    if (k < 0) {
      const half = this.w[c] / 2;
      k = this.cell(this.x0[c] + (q & 1 ? half : 0), this.y0[c] + (q & 2 ? half : 0), half);
      this.kids[c * 4 + q] = k;
    }
    return k;
  }

  build(xs: Float64Array, ys: Float64Array, n: number): void {
    this.size = 0;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < n; i++) {
      if (xs[i] < minX) minX = xs[i];
      if (xs[i] > maxX) maxX = xs[i];
      if (ys[i] < minY) minY = ys[i];
      if (ys[i] > maxY) maxY = ys[i];
    }
    const w = Math.max(maxX - minX, maxY - minY, 1) * 1.001;
    const root = this.cell(minX, minY, w);
    for (let i = 0; i < n; i++) {
      let c = root;
      for (let depth = 0; ; depth++) {
        const hasKids = this.kids[c * 4] >= 0 || this.kids[c * 4 + 1] >= 0 || this.kids[c * 4 + 2] >= 0 || this.kids[c * 4 + 3] >= 0;
        if (!hasKids && this.leaf[c] < 0) {
          this.leaf[c] = i;
          break;
        }
        if (!hasKids) {
          const j = this.leaf[c];
          if (depth > 40 || (xs[j] === xs[i] && ys[j] === ys[i])) break; // coincident points: the one already there stands for both
          this.leaf[c] = -1;
          const kj = this.child(c, this.quadrant(c, xs[j], ys[j]));
          this.leaf[kj] = j;
        }
        c = this.child(c, this.quadrant(c, xs[i], ys[i]));
      }
    }
    // centre of mass, children before parents (cells are created parent-first, so walk backwards)
    for (let c = this.size - 1; c >= 0; c--) {
      let m = 0, sx = 0, sy = 0;
      const j = this.leaf[c];
      if (j >= 0) {
        m = 1;
        sx = xs[j];
        sy = ys[j];
      }
      for (let q = 0; q < 4; q++) {
        const k = this.kids[c * 4 + q];
        if (k >= 0) {
          m += this.mass[k];
          sx += this.cx[k] * this.mass[k];
          sy += this.cy[k] * this.mass[k];
        }
      }
      this.mass[c] = m;
      this.cx[c] = m ? sx / m : 0;
      this.cy[c] = m ? sy / m : 0;
    }
  }
}

// --- the simulation -------------------------------------------------------------------------------------------

export function createLayout(n: number, links: LayoutLink[], options: LayoutOptions = {}): Layout {
  const linkDistance = options.linkDistance ?? 30;
  const charge = options.charge ?? -40;
  const theta2 = (options.theta ?? 0.9) ** 2;
  const gravity = options.gravity ?? 0.06;
  const velocityDecay = options.velocityDecay ?? 0.6;
  const alphaMin = options.alphaMin ?? 0.001;
  const alphaDecay = 1 - Math.pow(alphaMin, 1 / (options.coolingTicks ?? 300));

  const { xs, ys } = initialPositions(n);
  const vx = new Float64Array(n);
  const vy = new Float64Array(n);
  const degree = new Int32Array(n);
  const valid = links.filter((l) => l.a !== l.b && l.a >= 0 && l.b >= 0 && l.a < n && l.b < n);
  for (const l of valid) {
    degree[l.a]++;
    degree[l.b]++;
  }
  const strength = new Float64Array(valid.length);
  const bias = new Float64Array(valid.length);
  valid.forEach((l, i) => {
    strength[i] = 1 / Math.max(1, Math.min(degree[l.a], degree[l.b]));
    bias[i] = degree[l.a] / (degree[l.a] + degree[l.b]);
  });
  const tree = new Quadtree(n);
  const stack = new Int32Array(256);

  const layout: Layout = {
    xs,
    ys,
    alpha: 1,
    settled: n === 0,
    step(count: number): number {
      let ran = 0;
      while (ran < count && !layout.settled) {
        tick();
        ran++;
        if (layout.alpha < alphaMin) layout.settled = true;
      }
      return ran;
    },
  };

  function tick(): void {
    const alpha = (layout.alpha += (0 - layout.alpha) * alphaDecay);
    // link springs
    for (let i = 0; i < valid.length; i++) {
      const a = valid[i].a, b = valid[i].b;
      let dx = xs[b] + vx[b] - xs[a] - vx[a] || 1e-6;
      let dy = ys[b] + vy[b] - ys[a] - vy[a] || 1e-6;
      const d = Math.sqrt(dx * dx + dy * dy);
      const f = ((d - linkDistance) / d) * alpha * strength[i];
      dx *= f;
      dy *= f;
      vx[b] -= dx * bias[i];
      vy[b] -= dy * bias[i];
      vx[a] += dx * (1 - bias[i]);
      vy[a] += dy * (1 - bias[i]);
    }
    // repulsion between all nodes (Barnes-Hut)
    tree.build(xs, ys, n);
    for (let i = 0; i < n; i++) {
      const px = xs[i], py = ys[i];
      let sp = 0;
      stack[sp++] = 0;
      while (sp > 0) {
        const c = stack[--sp];
        const m = tree.mass[c];
        if (m === 0) continue;
        let dx = tree.cx[c] - px;
        let dy = tree.cy[c] - py;
        let l = dx * dx + dy * dy;
        const leafPoint = tree.leaf[c];
        if (leafPoint >= 0) {
          if (leafPoint === i) continue;
        } else if (tree.w[c] * tree.w[c] < theta2 * l) {
          // far enough: the whole cell acts as one body at its centre of mass
        } else {
          for (let q = 0; q < 4; q++) {
            const k = tree.kids[c * 4 + q];
            if (k >= 0) stack[sp++] = k;
          }
          continue;
        }
        if (dx === 0) dx = 1e-6 * (i % 2 ? 1 : -1);
        if (dy === 0) dy = 1e-6 * (i % 3 ? 1 : -1);
        l = dx * dx + dy * dy;
        if (l < 1) l = Math.sqrt(l);
        const k = (charge * alpha * m) / l;
        vx[i] += dx * k;
        vy[i] += dy * k;
      }
    }
    // pull to the middle, then move
    for (let i = 0; i < n; i++) {
      vx[i] -= xs[i] * gravity * alpha;
      vy[i] -= ys[i] * gravity * alpha;
      vx[i] *= velocityDecay;
      vy[i] *= velocityDecay;
      xs[i] += vx[i];
      ys[i] += vy[i];
    }
  }

  return layout;
}

/** Runs a layout to rest in one go (tests, and the first paint of a small graph). */
export function settle(layout: Layout, maxTicks = 400): Layout {
  layout.step(maxTicks);
  return layout;
}
